import { create } from 'zustand';
import {
  resolveOrRehydratePageComponent,
  resolvePageComponent,
  unregisterPageComponent,
} from '../plugin/pageRegistry.js';
import { describeRuntimeWindow, type RuntimeWindowClass } from '../plugin/runtimeWindowClasses.js';
import { DM_ACTIVITY_ROUTE } from '../modules/pm/dmActivityUrl.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export type WindowId = string;

/**
 * Pending mutate request dispatched from chat via mutate_window AI tool.
 * ComposerWindow instances check this on change and trigger their mutation flow
 * when they match via windowId (exact) or windowName (fuzzy).
 * Cleared by the matching window after it starts the mutation stream.
 */
export interface PendingMutateRequest {
  requestId: string;
  windowId?: string;
  windowName?: string;
  intent: string;
  dispatchedAt: string;
}

export type SnapDirection = 'left' | 'right' | 'top' | 'bottom' | 'maximize';

// ─── Dock groups (G7-W PR-4, gh#2059) ─────────────────────────────────────────
//
// A dock group is a PAIR of windows that tile the viewport with a movable seam between
// them. N-way tiling trees are an explicit NON-GOAL: every action below either produces a
// two-member group or produces none, and the untrusted reader (`applyLayout`) dissolves
// anything that is not exactly a complementary pair.
//
// WHY THE VIEWPORT AND NOT THE TARGET WINDOW'S RECT. The compositor already defines exactly
// one derived-geometry vocabulary — `snapWindow`'s viewport halves and `maximizeWindow`'s
// viewport rect. A group that tiles the viewport is expressible in that same vocabulary; a
// group that tiles an arbitrary rect would introduce a second, parallel geometry system
// whose frame has to be carried on the wire, clamped, and repaired independently. Tiling the
// viewport also makes the snapshot exact across screen sizes for free: the pair is recomputed
// against the APPLYING viewport exactly like a maximized window, so a group captured at
// 3840×2160 lands correctly at 1280×800 and no attacker-chosen group frame exists to abuse.
// The generalisation this forecloses — an arbitrary group frame, and with it N-way trees — is
// tracked as its own issue rather than half-built here.

/**
 * Every half a split can name. This is the vocabulary PR-1 reserved under the name
 * `LayoutDockSlot`; PR-4 gives it a reader on both sides of the snapshot, and G7-D
 * (gh#2521) gives it a spelling on the address.
 *
 * A runtime ARRAY and not merely a type union, because the `?win=` contract has to
 * ENUMERATE these halves to build its own suffix vocabulary from them. A second list over
 * there would be a second closed set, and the two would disagree the first time this one
 * grew: the store would dock to a half the address could not spell, silently, in exactly
 * the direction a shared link is supposed to survive.
 */
export const LAYOUT_DOCK_SLOTS = ['left', 'right', 'top', 'bottom'] as const;

/** Which half of the split a window occupies. */
export type LayoutDockSlot = (typeof LAYOUT_DOCK_SLOTS)[number];

/** The closed edge set, as a `Set` so no `Object.prototype` member can pose as an edge. */
const DOCK_EDGES: ReadonlySet<string> = new Set<string>(LAYOUT_DOCK_SLOTS);

/**
 * The opposite half — the edge a group's OTHER member must occupy. A `Map` rather than an
 * object literal on purpose: this is read with a key that came off the wire, and
 * `literal['constructor']` is a truthy hit.
 */
const DOCK_COMPLEMENT: ReadonlyMap<string, LayoutDockSlot> = new Map([
  ['left', 'right'],
  ['right', 'left'],
  ['top', 'bottom'],
  ['bottom', 'top'],
] as Array<[LayoutDockSlot, LayoutDockSlot]>);

/**
 * The complement of `edge`, or `undefined` for anything outside the vocabulary.
 *
 * Exported so the `?win=` contract pairs its tokens against the SAME table this store docks
 * by. A complement table written a second time over there would be a second answer to "which
 * two halves make a split", and an address would then spell a pair the store dissolves on
 * arrival — a link that opens a different desktop than the one it was copied from, which is
 * the failure gh#2521 exists to close rather than to reproduce one layer up.
 *
 * Takes `string` on purpose: every caller is asking about a value that came off a wire.
 */
export function dockComplementOf(edge: string): LayoutDockSlot | undefined {
  return DOCK_COMPLEMENT.get(edge);
}

/**
 * The share a fresh group gives its LEAD member (`left` / `top`) — an even split. The trailing
 * member takes `1 - ratio`. At 0.5 the two readings coincide; they stop coinciding the moment
 * this constant does, so the member it names has to be the one `dockWindow` hands it to.
 */
const DOCK_DEFAULT_RATIO = 0.5;

/** Does this edge split the viewport horizontally (vertical seam)? */
function isHorizontalEdge(edge: LayoutDockSlot): boolean {
  return edge === 'left' || edge === 'right';
}

/**
 * Does this edge LEAD its axis — own the `[0, seam)` side?
 *
 * The seam is computed once, from the leading member's share, and both members derive their
 * extent from it (`lead` gets `seam`, `trail` gets `axis - seam`). That is what makes the two
 * halves tile EXACTLY: computing each extent independently as `floor(axis * ownRatio)` leaves
 * a one-pixel gap whenever the two floors do not sum back to `axis`.
 */
function isLeadingEdge(edge: LayoutDockSlot): boolean {
  return edge === 'left' || edge === 'top';
}

/** A window's membership in a dock group. Both members share `groupId`. */
export interface WindowDock {
  /** Opaque group identity. Minted by the store — never carried in from a payload. */
  groupId: string;
  /** This window's half of the split. */
  edge: LayoutDockSlot;
  /**
   * This window's OWN share of the split axis, in (0,1). The two members are complementary
   * (`a.ratio + b.ratio === 1`); every writer in this module updates both in one `set()`, and
   * `applyLayout` re-derives the trailing share so untrusted input cannot land a pair that
   * gaps or overlaps.
   */
  ratio: number;
}

export interface WindowState {
  id: WindowId;
  pluginSlug: string;
  pageRoute: string;
  /**
   * Component key from pageComponentRegistry (e.g. 'ChatPage', 'NotesPage').
   * When set, DomWindow renders the plugin component via PluginWindowAdapter.
   * When absent, DomWindow falls back to demo content.
   */
  pageKey?: string;
  /**
   * gh#1574 — the window owns a per-OPEN DYNAMIC pageKey, registered by a desktop launcher
   * (openSpecWindow / openComposerWindow / openCompositionWindow) keyed by a fresh UUID.
   * When true, `removeWindow` unregisters `pageKey` from `pageComponentRegistry` on close so a
   * long session does not leak one lazy-component reference per open. Only set for per-open
   * UNIQUE keys — never for keys shared across windows (unregistering a shared key would drop a
   * still-live sibling). minimize/restore keep the window in the store, so the key survives;
   * only close/remove tears it down.
   */
  ownsPageKey?: boolean;
  title: string;
  pos: { x: number; y: number };
  size: { width: number; height: number };
  /** Position before maximize/snap/dock — used for restore */
  savedPos?: { x: number; y: number };
  /** Size before maximize/snap/dock — used for restore */
  savedSize?: { width: number; height: number };
  zIndex: number;
  isMinimized: boolean;
  isMaximized: boolean;
  isFocused: boolean;
  /** Faz 7: per-window AI sidebar toggle */
  agentSlotOpen?: boolean;
  /**
   * G7-W PR-4 — membership in a pairwise dock group. Present ⇒ `pos`/`size` are DERIVED from
   * the split and `savedPos`/`savedSize` hold the frame the window entered the group from.
   *
   * A window is in at most ONE derived state: docking clears `isMaximized`, and
   * maximize/minimize dissolve the group first. Never set by `addWindow` — a window is born
   * free and docks only through {@link DesktopState.dockWindow}.
   */
  dock?: WindowDock;
}

export type LayoutMode = 'free' | 'snap' | 'spatial';

// ─── Layout snapshot (G7-W PR-1, gh#2059) ─────────────────────────────────────

/**
 * Snapshot schema version. `applyLayout` silently REFUSES any other value, so this is the
 * forward-compat gate: a future breaking shape change bumps this number and old readers
 * no-op instead of mis-applying a shape they do not understand.
 *
 * PR-4's `dock` did NOT bump it, and the test that says so states the criterion: a bump is
 * owed when an old reader would MIS-APPLY the new shape, not merely when the shape grows.
 * `dock` is additive and optional, and a v1 reader that has never heard of it drops the key
 * and opens both windows at the frames the entry already carries — which are valid,
 * clamped, on-screen frames. Degradation, not misreading. Since two versions of this app can
 * face the same `workspace_layouts` row across a rolling deploy, that distinction is the
 * whole point of the gate: bumping for an additive field would make the OLDER tab refuse the
 * layout entirely, which is strictly worse than restoring it unsplit.
 */
export const LAYOUT_SNAPSHOT_VERSION = 1;

/** One window inside a {@link LayoutSnapshot}. Identity is the registry `pageKey`. */
export interface LayoutSnapshotWindow {
  /** Registry key (`pageComponentRegistry`) — the snapshot's identity for this window. */
  pageKey: string;
  pluginSlug: string;
  pageRoute: string;
  /**
   * The window's frame in whatever state is NOT derived.
   *
   * ONE rule covers every case: `pos`/`size` is the RESTORE frame exactly when the entry also
   * carries a DISCRIMINATOR from which the on-screen frame can be re-derived — `isMaximized`
   * (the viewport rect) or {@link dock} (the split rect). Otherwise it is the on-screen frame
   * itself, because there is nothing to derive it from. A viewport-dependent rect is never
   * written down: `applyLayout` recomputes it against the CURRENT viewport, the same rule
   * `maximizeWindow` and `dockWindow` use at the moment of the action.
   *
   * NOT trusted on the way in: `applyLayout` clamps it to the applying viewport, so a layout
   * captured on a 3840px screen cannot push a window off a 1280px one.
   */
  pos: { x: number; y: number };
  size: { width: number; height: number };
  isMinimized: boolean;
  isMaximized: boolean;
  /**
   * G7-W PR-4 — the window's half of a pairwise dock group, and the discriminator that makes
   * its on-screen frame derivable (see {@link pos}).
   *
   * Captured only when BOTH members of the group are in the same snapshot, and applied only
   * when both survive every gate — a half-recorded group would be a group `applyLayout`
   * refuses, and capture must never record what apply refuses. `groupId` is re-minted on
   * apply, so a payload string never becomes an identity inside the store.
   */
  dock?: WindowDock;
}

/**
 * A portable description of the compositor layout — the OUTPUT contract of `captureLayout`.
 * `applyLayout` deliberately does NOT take this type (it takes `unknown`): everything it
 * receives is untrusted, whatever a caller's types claim.
 *
 * No title field: a window's title is not layout, it is a property of the page, and the
 * canonical source is the viewer's own gate — `LayoutAllowedPage.label` for a listed page,
 * the class's key-derived label for a runtime one. Carrying it in the snapshot would let a
 * persisted or URL-supplied string paint an arbitrary title bar.
 */
export interface LayoutSnapshot {
  version: typeof LAYOUT_SNAPSHOT_VERSION;
  windows: LayoutSnapshotWindow[];
  /** pageKeys of the non-minimized windows, bottom → top (tail = focused). */
  focusOrder: string[];
}

/**
 * One page the CURRENT VIEWER is allowed to open, structurally satisfied by
 * `useWindowablePages()` — which is the surface that already applies role + permission
 * filtering (`filterByRole` / `filterByPermissions` / `canAccessRole`).
 */
export interface LayoutAllowedPage {
  pageKey: string;
  /** Canonical, human-readable window title for this page. */
  label: string;
}

/**
 * The authorization gate BOTH snapshot verbs read.
 *
 * A layout is a second offering path into the same pages the launcher offers, so it must not
 * be a weaker door: the component registry only answers "does this key exist", never "may
 * THIS viewer open it" (it is a module-level map, identical for an admin and a guest).
 * Passing the viewer's own `useWindowablePages()` output makes capture and apply agree on one
 * eligibility axis — a key the viewer may not open is neither recorded nor restored — and the
 * registry probe stays as the independent identity check on top.
 *
 * {@link LayoutGateOptions.runtimeClasses} is the same door widened to keys that do not exist
 * when the allowlist is built, and it is widened on the SAME axis: a class carries the
 * viewer filter of the launcher path that mints its keys. Identity for a class member is the
 * class's own parse — a key it cannot address is refused exactly as an unregistered one is.
 */
export interface LayoutGateOptions {
  allowedPages: readonly LayoutAllowedPage[];
  /**
   * Is `allowedPages` COMPLETE right now? Caller obligation, and not optional.
   *
   * The allowlist is assembled from two sources with different timing: `CORE_WINDOWABLE_PAGES`
   * is a synchronous module constant, while the manifest pages come from `useModules`, which
   * starts at `plugins: []` and stays there while loading — and, on a failed fetch, FOREVER
   * (it calls `setError` without ever clearing or filling `plugins`). So a half-built
   * allowlist is not empty, it is SHORT: core pages present, ChatPage / NotesPage / AIPage
   * missing. An incomplete list is indistinguishable from a restrictive one.
   *
   * The two verbs fail in opposite directions on that list, which is why the signal has to be
   * explicit rather than inferred from `allowedPages.length`:
   * - `applyLayout` merely opens fewer windows — recoverable, and the snapshot is untouched;
   * - `captureLayout` would RECORD the short list as if it were the layout, and once PR-2
   *   persists that, the windows it could not see are gone from the saved layout for good.
   *
   * Both therefore refuse unless this is `true`. Callers on the `useModules` path derive it as
   * `!loading && !error`; a caller with a synchronous allowlist passes `true` outright.
   */
  ready: boolean;
  /**
   * Runtime window CLASSES this viewer may open — the second half of the same eligibility
   * question `allowedPages` answers (gh#2517).
   *
   * `allowedPages` can only ever list keys that EXIST when the list is built, so it cannot
   * carry a key minted at runtime (`UserViewWindow-<id>`): such a window was dropped by
   * capture, by apply and by the URL writer alike, and a saved workspace lost it on every
   * reload. A class admits a whole FAMILY of keys by prefix, so a viewer's eligibility can
   * be expressed for keys nobody has registered yet.
   *
   * ALREADY FILTERED for this viewer, exactly like `allowedPages`: `permittedRuntimeWindowClasses`
   * applies `permitted(viewer)` at the one place the roster is derived, so the store never
   * decides authorization and never learns who the viewer is.
   *
   * OPTIONAL, and absent means NONE — fail-closed, and in the recoverable direction: a caller
   * that omits it restores fewer windows (today's behaviour), never more.
   */
  runtimeClasses?: readonly RuntimeWindowClass[];
}

// ─── Window geometry bounds ───────────────────────────────────────────────────
//
// Mirrors of the compositor's own constraints, re-declared here because the store may not
// import from the component layer (useResizeHandles imports THIS module). desktopStore.test
// pins each one against its origin, so a drift shows up as a failing test, not as a snapshot
// that silently violates what a resize handle would allow.

/** Minimum window width — mirrors `useResizeHandles.MIN_WIDTH`. */
export const MIN_WINDOW_WIDTH = 240;
/** Minimum window height — mirrors `useResizeHandles.MIN_HEIGHT`. */
export const MIN_WINDOW_HEIGHT = 160;
/**
 * Title-bar height — mirrors `WindowTopBar.TOP_BAR_H`. DomWindow draws the bar ABOVE `pos.y`
 * (`top: -TOP_BAR_H`), so a window at y < TOP_BAR_H has its only drag handle off-screen.
 */
export const WINDOW_TITLE_BAR_H = 36;

interface DesktopState {
  windows: Record<WindowId, WindowState>;
  focusStack: WindowId[]; // tail = top (most recently focused)
  layoutMode: LayoutMode;
  fpsCounterEnabled: boolean;
  /**
   * Faz 10 dilim 3 — chat mutate_window tool dispatch'i.
   * null when no pending request. ComposerWindow instances watch this field
   * and consume/clear it when they match the target.
   */
  pendingMutateRequest: PendingMutateRequest | null;

  // actions
  /**
   * `dock` is excluded from the seed: a window is born free, and joins a group only through
   * {@link dockWindow}, which is the one place that can write BOTH halves of a pair.
   */
  addWindow: (w: Omit<WindowState, 'id' | 'zIndex' | 'isFocused' | 'dock'>) => WindowId;
  removeWindow: (id: WindowId) => void;
  focusWindow: (id: WindowId) => void;
  moveWindow: (id: WindowId, pos: { x: number; y: number }) => void;
  resizeWindow: (id: WindowId, size: { width: number; height: number }) => void;
  setLayoutMode: (mode: LayoutMode) => void;
  toggleFpsCounter: () => void;

  // Faz 5 — window manager actions
  closeWindow: (id: WindowId) => void;
  minimizeWindow: (id: WindowId) => void;
  maximizeWindow: (id: WindowId) => void;
  restoreWindow: (id: WindowId) => void;
  /** Cycle focus to next window in stack (forward) */
  cycleFocus: () => void;
  /** Snap to a workspace edge or maximize */
  snapWindow: (id: WindowId, direction: SnapDirection) => void;
  /** Blur (unfocus) the currently active window */
  blurActiveWindow: () => void;
  // Faz 7 — per-window AI sidebar
  /** Toggle the per-window AI sidebar open/closed */
  toggleAgentSlot: (id: WindowId) => void;

  // G7-W PR-4 — pairwise split/dock
  /**
   * Put `id` on `edge` of `targetId`: the pair tiles the viewport with `id` taking `edge` and
   * `targetId` the complementary half, linked by a movable seam.
   *
   * PAIRWISE, and a third window TAKES rather than joins: if either window is already in a
   * group, that group is dissolved first under the `survivor-keeps` rule, so the abandoned
   * partner inherits the space and no group ever exceeds two members. Refused (no-op) when
   * either window is missing, when they are the same window, or when either is minimized —
   * a docked window is on screen by definition.
   */
  dockWindow: (id: WindowId, targetId: WindowId, edge: LayoutDockSlot) => void;
  /** Unsplit the group `id` is in: both members return to the frames they docked from. */
  undockWindow: (id: WindowId) => void;
  /**
   * Move the seam. `ratio` is the LEADING member's share (the `left`/`top` half), which is
   * what a seam at `x` pixels reads as `x / viewportWidth`; the trailing member is written the
   * complement in the same update, so the two halves cannot drift apart. Clamped so neither
   * half falls below the compositor's minimum window size.
   */
  setDockRatio: (groupId: string, ratio: number) => void;
  /**
   * Re-derive the viewport-derived frames — maximized and docked — against the CURRENT
   * viewport; saved frames are untouched (gh#2518). A snapped half carries no discriminator
   * yet, so it keeps its frame (gh#2523).
   */
  fitDerivedFramesToViewport: () => void;

  // Faz 10 — chat mutate request
  /** Set a pending mutate request from chat AI tool dispatch */
  setPendingMutateRequest: (req: PendingMutateRequest) => void;
  /** Clear the pending mutate request after it has been consumed */
  clearPendingMutateRequest: () => void;

  // G7-W PR-1 — layout snapshot
  /**
   * Capture the layout of the windows this viewer is allowed to open, or `null` when the
   * caller's allowlist is not ready yet — a short list would be recorded as if it were the
   * whole layout (see {@link LayoutGateOptions.ready}).
   */
  captureLayout: (options: LayoutGateOptions) => LayoutSnapshot | null;
  /**
   * Apply a layout snapshot to the store in ONE atomic write. Takes `unknown` on purpose —
   * the payload arrives from persistence (PR-2) or the URL (PR-3), so it is validated at
   * runtime rather than trusted from a type annotation.
   */
  applyLayout: (snapshot: unknown, options: LayoutGateOptions) => void;
  /**
   * Drop the session's windows on sign-out (gh#2561): titles carry item/view names, and a
   * soft `/login` navigation would hand them to the next user. Preferences stay.
   */
  reset: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeZIndices(focusStack: WindowId[]): Record<WindowId, number> {
  const result: Record<WindowId, number> = {};
  focusStack.forEach((id, index) => {
    result[id] = index + 1; // 1-based; tail = highest z
  });
  return result;
}

// ─── Layout snapshot helpers (G7-W PR-1) ──────────────────────────────────────

/** Viewport rect used for maximized geometry — mirrors `snapWindow`'s SSR-safe fallback. */
function viewportSize(): { width: number; height: number } {
  return {
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080,
  };
}

function isPoint(v: unknown): v is { x: number; y: number } {
  if (typeof v !== 'object' || v === null) return false;
  const p = v as { x?: unknown; y?: unknown };
  return Number.isFinite(p.x) && Number.isFinite(p.y);
}

function isSize(v: unknown): v is { width: number; height: number } {
  if (typeof v !== 'object' || v === null) return false;
  const s = v as { width?: unknown; height?: unknown };
  return (
    typeof s.width === 'number' &&
    Number.isFinite(s.width) &&
    s.width > 0 &&
    typeof s.height === 'number' &&
    Number.isFinite(s.height) &&
    s.height > 0
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Fit a snapshot frame to the viewport that is APPLYING it.
 *
 * A snapshot outlives the screen it was taken on: captured at 3840×2160 and restored on a
 * 1280×800 laptop, verbatim geometry puts windows partly or wholly outside the viewport, and
 * because the title bar is the only drag handle (`DomWindow` renders it at `top: -TOP_BAR_H`,
 * i.e. ABOVE `pos.y`) an off-screen window cannot be dragged back — it is simply lost. The
 * same repair covers hostile input: PR-3 reads geometry off the URL, where `x: 1e9` costs
 * nothing to write.
 *
 * Size is capped at the viewport — height at `viewport.height - WINDOW_TITLE_BAR_H`, because
 * the bar is drawn ABOVE `pos.y` and so occupies height the box itself does not report — and
 * never below the minimum a resize handle would allow. Position is then confined so the whole
 * window, title bar included, stays on screen.
 *
 * This is the RESTORE frame only. The maximized rect `applyLayout` computes is a mirror of
 * `maximizeWindow` (full viewport at 0,0, title bar overflowing the top edge) and deliberately
 * does not come through here — a restored maximized window must look like a maximized window.
 */
function clampToViewport(
  pos: { x: number; y: number },
  size: { width: number; height: number },
): { pos: { x: number; y: number }; size: { width: number; height: number } } {
  const viewport = viewportSize();
  const width = clamp(size.width, MIN_WINDOW_WIDTH, Math.max(MIN_WINDOW_WIDTH, viewport.width));
  const height = clamp(
    size.height,
    MIN_WINDOW_HEIGHT,
    // Reserve the title bar's own strip: the window's visual extent is [y - TOP_BAR_H, y + h].
    Math.max(MIN_WINDOW_HEIGHT, viewport.height - WINDOW_TITLE_BAR_H),
  );

  // A window taller/wider than the viewport (viewport smaller than the minimum) still gets a
  // non-empty range: the max never falls below the min.
  const maxX = Math.max(0, viewport.width - width);
  const maxY = Math.max(WINDOW_TITLE_BAR_H, viewport.height - height);

  return {
    pos: {
      x: clamp(pos.x, 0, maxX),
      // Never above the title bar's own height — that is what keeps the drag handle reachable.
      y: clamp(pos.y, WINDOW_TITLE_BAR_H, maxY),
    },
    size: { width, height },
  };
}

// ─── Dock geometry (G7-W PR-4) ────────────────────────────────────────────────

/**
 * Confine a dock ratio so NEITHER half falls below the minimum a resize handle would allow.
 *
 * The bound is derived from the viewport rather than fixed, because "10% of the screen" is a
 * usable window at 3840px and an unusable sliver at 1280px. When the viewport cannot fit two
 * minimums at all the range inverts, and an inverted `clamp` returns its `max` — a value
 * BELOW the min it was asked to respect. That silent inversion is exactly the kind of
 * fail-open a seam drag would walk into, so it is caught and answered with an even split.
 */
function clampDockRatio(ratio: number, horizontal: boolean): number {
  if (!Number.isFinite(ratio)) return 0.5;
  const viewport = viewportSize();
  const axis = horizontal ? viewport.width : viewport.height;
  const minimum = horizontal ? MIN_WINDOW_WIDTH : MIN_WINDOW_HEIGHT;
  const lo = minimum / axis;
  const hi = 1 - lo;
  if (!(lo < hi)) return 0.5;
  return clamp(ratio, lo, hi);
}

/** A window frame, as the compositor draws it. */
interface WindowRect {
  pos: { x: number; y: number };
  size: { width: number; height: number };
}

/**
 * BOTH halves of a dock group, derived from ONE seam number.
 *
 * The group — not the member — is the unit of this computation, and that is the whole point.
 * Deriving each half from its own share means computing `floor(axis * L)` on one side and
 * `axis - floor(axis * (1 - (1 - L)))` on the other, and `1 - (1 - L)` is NOT `L` in IEEE-754:
 * at 1920px the two sides disagree for 12.6% of the pixel positions a seam drag visits, each
 * one a one-pixel column of desktop showing between two windows that claim to tile, or a
 * one-pixel overlap. Taking `seam` once and giving the trailing member `axis - seam` makes the
 * tiling exact by construction rather than by luck of the arithmetic.
 *
 * `leadRatio` is always the LEADING member's share (`left`/`top`). `WindowDock.ratio` is
 * therefore a display and wire value only: nothing derives geometry from the trailing copy.
 *
 * Position follows `snapWindow`'s convention exactly (`y: 0` for a full-height half, i.e. the
 * title bar overflows the top edge), so a docked half and a snapped half are the same rect.
 */
function dockGroupRects(
  leadEdge: LayoutDockSlot,
  leadRatio: number,
): { lead: WindowRect; trail: WindowRect } {
  const viewport = viewportSize();
  const horizontal = isHorizontalEdge(leadEdge);
  const axis = horizontal ? viewport.width : viewport.height;
  const seam = Math.floor(axis * leadRatio);

  return horizontal
    ? {
        lead: { pos: { x: 0, y: 0 }, size: { width: seam, height: viewport.height } },
        trail: { pos: { x: seam, y: 0 }, size: { width: axis - seam, height: viewport.height } },
      }
    : {
        lead: { pos: { x: 0, y: 0 }, size: { width: viewport.width, height: seam } },
        trail: { pos: { x: 0, y: seam }, size: { width: viewport.width, height: axis - seam } },
      };
}

/** The leading edge of the axis `edge` sits on — the half that owns `[0, seam)`. */
function leadEdgeOfAxis(edge: LayoutDockSlot): LayoutDockSlot {
  return isHorizontalEdge(edge) ? 'left' : 'top';
}

/**
 * The two rects {@link DesktopState.dockWindow} would produce for a drop on `edge`.
 *
 * The compositor's drop preview draws exactly these, and it is the ONE geometry export this
 * module offers the view layer: a `0.5` and a complement-lookup written a second time over
 * there is a preview that keeps promising an even split after the default or the minimum-size
 * clamp moves, i.e. a preview that shows one layout while the drop produces another.
 */
export function dockSplitPreview(edge: LayoutDockSlot): { dragged: WindowRect; target: WindowRect } {
  const leadRatio = clampDockRatio(DOCK_DEFAULT_RATIO, isHorizontalEdge(edge));
  const { lead, trail } = dockGroupRects(leadEdgeOfAxis(edge), leadRatio);
  return isLeadingEdge(edge) ? { dragged: lead, target: trail } : { dragged: trail, target: lead };
}

/** Ids of every window in `groupId`, in `windows` insertion order. */
function dockGroupMemberIds(
  windows: Record<WindowId, WindowState>,
  groupId: string,
): WindowId[] {
  return Object.entries(windows)
    .filter(([, win]) => win.dock?.groupId === groupId)
    .map(([id]) => id);
}

/** Leave the group and return to the frame the window docked FROM. */
function undockedWindow(win: WindowState): WindowState {
  return {
    ...win,
    dock: undefined,
    pos: win.savedPos ?? win.pos,
    size: win.savedSize ?? win.size,
    savedPos: undefined,
    savedSize: undefined,
  };
}

/**
 * Leave the group and take the frame the PAIR occupied — the viewport.
 *
 * `savedPos`/`savedSize` are deliberately KEPT: the survivor never asked to leave, so its
 * traffic-light restore must still return it to its own pre-dock frame.
 */
function expandedToGroupFrame(win: WindowState): WindowState {
  const viewport = viewportSize();
  return {
    ...win,
    dock: undefined,
    pos: { x: 0, y: 0 },
    size: { width: viewport.width, height: viewport.height },
  };
}

/**
 * Dissolve the group `id` belongs to. Pairwise, so removing either member ends the group.
 *
 * The two modes are not a style choice — they are two different things the operator did:
 *
 * - `return-both` — "unsplit". Nobody is leaving the screen, so the pair unlinks and BOTH
 *   members go back to the frames they docked from. This makes dock/undock an involution,
 *   and it is what `undockWindow`, a title-bar drag and `snapWindow` all mean.
 * - `survivor-keeps` — one member is leaving the screen (minimize / maximize / close). The
 *   one that stays keeps the space the pair was using, exactly as a split view collapses onto
 *   its remaining pane. The leaving member still returns to its own pre-dock frame, so
 *   restoring it later lands where it started rather than on a stale half.
 */
function dissolveDockGroup(
  windows: Record<WindowId, WindowState>,
  id: WindowId,
  mode: 'return-both' | 'survivor-keeps',
): Record<WindowId, WindowState> {
  const win = windows[id];
  if (!win?.dock) return windows;

  const { groupId } = win.dock;
  const next = { ...windows, [id]: undockedWindow(win) };
  for (const memberId of dockGroupMemberIds(windows, groupId)) {
    if (memberId === id) continue;
    const member = windows[memberId];
    next[memberId] = mode === 'return-both' ? undockedWindow(member) : expandedToGroupFrame(member);
  }
  return next;
}

/**
 * Fail-closed integrity net for `applyLayout`, the one verb that writes many windows at once.
 *
 * Every action in this store maintains the pairwise invariant on its own, but a bulk write can
 * strand a group: a snapshot that re-lays-out ONE member of a live pair leaves the other
 * holding a `dock` whose partner is gone. An orphan keeps its current frame — it is already a
 * valid on-screen rect and nothing should jump — and simply loses the flag, so the store never
 * claims a split the compositor cannot draw.
 */
function sweepDockGroups(windows: Record<WindowId, WindowState>): Record<WindowId, WindowState> {
  const counts = new Map<string, number>();
  for (const win of Object.values(windows)) {
    if (win.dock) counts.set(win.dock.groupId, (counts.get(win.dock.groupId) ?? 0) + 1);
  }
  let swept: Record<WindowId, WindowState> | null = null;
  for (const [id, win] of Object.entries(windows)) {
    if (!win.dock || counts.get(win.dock.groupId) === 2) continue;
    swept ??= { ...windows };
    swept[id] = { ...win, dock: undefined };
  }
  return swept ?? windows;
}

/** One window ranked for snapshot identity resolution. */
interface RankedWindow {
  id: WindowId;
  win: WindowState;
  pageKey: string;
  /** focusStack index; -1 when the window is not stacked (minimized windows are popped). */
  rank: number;
  /** `windows` insertion index — deterministic tie-break for un-stacked windows. */
  insertion: number;
}

/**
 * Resolve, per pageKey, the ONE live window that may REPRESENT it — the one highest in the
 * focus stack, ties broken by insertion order (most recently added wins) so two minimized
 * siblings still pick deterministically. `captureLayout` reads it to decide which window to
 * record, `applyLayout` to decide which window to re-use, so both agree.
 *
 * This is NOT the eligibility axis. What may enter or leave a snapshot is decided by the
 * viewer's own gate — the `allowedPages` allowlist, or the runtime CLASS roster that carries
 * the same viewer filter for keys minted at runtime — and both verbs read that gate the same
 * way round, because eligibility split across two different axes is how a window gets
 * captured and then silently dropped on apply.
 *
 * What this function does own is representability:
 * - a window with no `pageKey` renders demo content and has nothing to represent;
 * - an `ownsPageKey` window (gh#1574) holds a per-OPEN dynamic key that is unregistered when
 *   it closes, so it can be neither re-created nor handed to another snapshot entry. Those
 *   keys are surfaced separately by {@link collectOwnedPageKeys} so `applyLayout` can REFUSE
 *   an entry whose key is currently owned rather than open an unowned duplicate of it.
 */
function rankRepresentableWindows(
  windows: Record<WindowId, WindowState>,
  focusStack: WindowId[],
): Map<string, RankedWindow> {
  const stackIndex = new Map<WindowId, number>();
  focusStack.forEach((id, index) => stackIndex.set(id, index));

  const byPageKey = new Map<string, RankedWindow>();
  Object.entries(windows).forEach(([id, win], insertion) => {
    const pageKey = win.pageKey;
    if (!pageKey) return;
    if (win.ownsPageKey) return;

    const candidate: RankedWindow = {
      id,
      win,
      pageKey,
      rank: stackIndex.get(id) ?? -1,
      insertion,
    };
    const current = byPageKey.get(pageKey);
    if (
      !current ||
      candidate.rank > current.rank ||
      (candidate.rank === current.rank && candidate.insertion > current.insertion)
    ) {
      byPageKey.set(pageKey, candidate);
    }
  });

  return byPageKey;
}

/**
 * The live window that REPRESENTS `pageKey` right now, or `undefined` — the launcher-side
 * reader of {@link rankRepresentableWindows} (gh#2517).
 *
 * A launcher that opens a page already on screen must RE-USE that window rather than add a
 * second one: `addWindow` does not dedupe, and a duplicate is not merely untidy — the snapshot
 * verbs keep exactly one window per pageKey, so the copy is silently absent from every saved
 * layout and every link, and closing the survivor is what makes it reappear. Asking the same
 * function the snapshot asks is what keeps "which window is THE window for this key" from
 * being answered two different ways.
 */
export function findRepresentativeWindow(pageKey: string): WindowState | undefined {
  const { windows, focusStack } = useDesktopStore.getState();
  return rankRepresentableWindows(windows, focusStack).get(pageKey)?.win;
}

/**
 * pageKeys currently OWNED by a live window (gh#1574 `ownsPageKey`).
 *
 * Such a key is registered only for as long as its window lives. Applying a snapshot entry
 * for it would open a second window sharing the key WITHOUT ownership — and the moment the
 * original closes, `removeWindow` unregisters the component out from under the copy, which
 * then renders "Plugin component not registered". Refuse the entry instead.
 */
function collectOwnedPageKeys(windows: Record<WindowId, WindowState>): Set<string> {
  const owned = new Set<string>();
  for (const win of Object.values(windows)) {
    if (win.ownsPageKey && win.pageKey) owned.add(win.pageKey);
  }
  return owned;
}

/**
 * Version gate + shape check for an incoming snapshot. Takes `unknown` on purpose: PR-3 will
 * hand this data straight off the URL, so nothing here may trust the declared TS type.
 * Returns null for anything this reader cannot apply (wrong version, wrong shape) → no-op.
 */
function normalizeSnapshot(input: unknown): { windows: unknown[]; focusOrder: string[] } | null {
  if (typeof input !== 'object' || input === null) return null;
  const s = input as { version?: unknown; windows?: unknown; focusOrder?: unknown };
  if (s.version !== LAYOUT_SNAPSHOT_VERSION) return null;
  if (!Array.isArray(s.windows)) return null;
  const focusOrder = Array.isArray(s.focusOrder)
    ? s.focusOrder.filter((key): key is string => typeof key === 'string')
    : [];
  return { windows: s.windows, focusOrder };
}

/** A snapshot entry that passed every gate, with its canonical label resolved. */
interface ApplicableEntry {
  pageKey: string;
  /**
   * Owning surface slug and full-page route. DERIVED from the key for a runtime class member;
   * carried by the payload for a listed page, whose allowlist row has neither to derive from
   * (gh#2551). `pluginSlug` reaches the screen through `PluginWindowAdapter`'s error panel, so
   * which of the two arms a window came through decides whether that string could be chosen.
   */
  pluginSlug: string;
  pageRoute: string;
  /**
   * Canonical title — the allowlist's label for a listed page, the class's key-derived one for
   * a runtime member. Never a string carried by the payload.
   */
  label: string;
  pos: { x: number; y: number };
  size: { width: number; height: number };
  isMinimized: boolean;
  isMaximized: boolean;
  /** Dock claim that passed shape validation. Pairing is decided later, across entries. */
  dock: WindowDock | null;
}

/**
 * Shape-check a dock claim off the wire, or null.
 *
 * `ratio` is refused at the OPEN interval: 0 and 1 both describe a member with no extent,
 * which is not a split but a window hidden behind another one. The clamp that follows only
 * narrows further, so a payload cannot reach it with a degenerate value to begin with.
 */
function toDockDescriptor(raw: unknown): WindowDock | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const d = raw as { groupId?: unknown; edge?: unknown; ratio?: unknown };
  if (typeof d.groupId !== 'string' || d.groupId.length === 0) return null;
  if (typeof d.edge !== 'string' || !DOCK_EDGES.has(d.edge)) return null;
  if (typeof d.ratio !== 'number' || !Number.isFinite(d.ratio)) return null;
  if (d.ratio <= 0 || d.ratio >= 1) return null;
  return { groupId: d.groupId, edge: d.edge as LayoutDockSlot, ratio: d.ratio };
}

/**
 * Page keys that have been RENAMED, old → new (DM W2 A1).
 *
 * ── A SNAPSHOT IS AN ADDRESS, AND A RENAME BREAKS IT SILENTLY ──────────────
 * Doors 2 and 4 below both key on `pageKey`, so a saved layout naming a key that no longer
 * exists is dropped by the fail-soft contract — silently, which is right for an uninstalled
 * plugin and wrong for a page that is still here under another name. The operator's window
 * simply does not come back, with nothing on screen saying why.
 *
 * The forward is the same decision `App.tsx` makes for the ROUTE (`/dm/runs` →
 * `/dm/activity`): a rename is not a retirement, and both of a surface's addresses — its URL
 * and its layout key — have to survive it or only half the operator's saved state does.
 *
 * ── IT IS NOT A SECOND ALLOWLIST ───────────────────────────────────────────
 * The mapping runs BEFORE the doors, so the NEW key is what authorization, ownership and
 * identity are all asked about. A viewer who may not open `DmActivityTab` does not get it by
 * having an old snapshot: the rename translates the ask, it does not answer it.
 *
 * An entry is REMOVED from this map when the old key stops appearing in stored layouts,
 * which nothing measures today — so entries stay until a retirement PR argues one out.
 */
export interface RenamedCorePage {
  pageKey: string;
  pluginSlug: string;
  pageRoute: string;
}

/**
 * ── THE WHOLE IDENTITY MOVES, NOT JUST THE KEY ─────────────────────────────
 * A snapshot entry carries `pluginSlug` and `pageRoute` beside the key, and the listed-page
 * arm below READS both off the payload (the allowlist is `{pageKey, label}` and has nothing
 * to derive them from). So a rename that forwarded the key alone would restore the window
 * with the RETIRED slug and route: `PluginWindowAdapter` renders the slug as visible text
 * and as its error-panel testid (`plugin-error-dm-runs`), and `aiContextBuilder` reports it
 * as the open surface's identity. Half a rename is a window whose component is one page and
 * whose name is another.
 *
 * ── THE DESCRIPTOR IS A LITERAL, AND THE PARITY IS MACHINERY ───────────────
 * It is NOT derived from `CORE_WINDOWABLE_PAGES` at runtime: that module reaches
 * `useWindowablePages`, which is a React hook module, and importing it here would pull hooks
 * into a zustand store. So the two are INDEPENDENT AXES bound by a test —
 * `desktopStore.test.ts` asserts this descriptor equals the launcher's row for the same key,
 * which is the shape `AGENT_RUN_RESULT_REF_KINDS` and its schema already use.
 */
const RENAMED_PAGE_KEYS: Readonly<Record<string, RenamedCorePage>> = {
  DmAgentRunPanel: {
    pageKey: 'DmActivityTab',
    pluginSlug: 'dm-activity',
    pageRoute: DM_ACTIVITY_ROUTE,
  },
};

/**
 * EXPORTED for the parity test above and `desktopUrl`'s default frame (dm.issue 1472).
 *
 * `Object.hasOwn`, not `?? undefined`: a stored key spelling `constructor` or `toString`
 * would otherwise read a function off the prototype and rename the window to it
 * ([[feedback_closed_map_prototype_key_fail_open]]).
 */
export function renamedCorePageOf(pageKey: string): RenamedCorePage | undefined {
  return Object.hasOwn(RENAMED_PAGE_KEYS, pageKey) ? RENAMED_PAGE_KEYS[pageKey] : undefined;
}

/**
 * Validate ONE snapshot entry and narrow it to something applicable, or null — a SILENT skip,
 * per the fail-soft contract. Four independent doors, in order:
 *
 *  1. shape — anything malformed (non-finite point, non-positive size, missing strings);
 *  2. AUTHORIZATION — the key must be in this viewer's allowlist, OR a well-formed member of
 *     a runtime CLASS this viewer may open. A snapshot is a second way to open a page, so
 *     both arms answer to the same role/permission filter the launcher uses;
 *  3. OWNERSHIP — the key must not be held right now by a live `ownsPageKey` window;
 *  4. IDENTITY — the key must resolve to an own registry entry (uninstalled plugin, stale
 *     dynamic key, `Object.prototype` member), rehydrating a class member that this session
 *     has not registered yet.
 *
 * ── WHY OWNERSHIP MOVED AHEAD OF IDENTITY ────────────────────────────────────
 * Both are pure refusals and the OUTCOME is the same in either order — but identity is no
 * longer side-effect free: the class arm REGISTERS the component it builds. Refusing an
 * owned key first means an entry this function was always going to drop can no longer write
 * to the registry on its way out. Authorization stays first for the stronger version of the
 * same argument: nothing unpermitted may cause a registration at all.
 *
 * Geometry is then clamped to the applying viewport (see {@link clampToViewport}).
 */
function toApplicableEntry(
  raw: unknown,
  allowedByPageKey: Map<string, LayoutAllowedPage>,
  ownedPageKeys: Set<string>,
  runtimeClasses: readonly RuntimeWindowClass[] | undefined,
): ApplicableEntry | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Record<string, unknown>;
  if (typeof e.pageKey !== 'string' || e.pageKey.length === 0) return null;
  // THE RENAME IS APPLIED FIRST, so every door below asks about the key this entry means
  // today — see {@link RENAMED_PAGE_KEYS}.
  const renamed = renamedCorePageOf(e.pageKey);
  const pageKey = renamed?.pageKey ?? e.pageKey;
  // Required of EVERY entry, including a class member whose slug and route are then derived
  // rather than read: this is the shape door, and an entry missing fields the format defines
  // is malformed whether or not this reader happens to need them.
  if (typeof e.pluginSlug !== 'string' || typeof e.pageRoute !== 'string') return null;
  if (!isPoint(e.pos) || !isSize(e.size)) return null;

  // Door 2 — AUTHORIZATION. The allowlist is asked FIRST, so a key that is a real page for
  // this viewer is never reinterpreted as a class member; a class only ever speaks for keys
  // the allowlist has no answer about.
  const allowed = allowedByPageKey.get(pageKey);
  const runtime = allowed ? undefined : describeRuntimeWindow(pageKey, runtimeClasses);
  if (!allowed && !runtime) return null;

  // Door 3 — OWNERSHIP.
  if (ownedPageKeys.has(pageKey)) return null;

  // Door 4 — IDENTITY.
  if (runtime) {
    if (!resolveOrRehydratePageComponent(pageKey, runtime.cls)) return null;
  } else if (!resolvePageComponent(pageKey)) return null;

  const geometry = clampToViewport(
    { x: e.pos.x, y: e.pos.y },
    { width: e.size.width, height: e.size.height },
  );

  return {
    pageKey,
    // ── CHROME: DERIVED for a class member, payload-carried for a listed page ──────
    //
    // The asymmetry is the allowlist's SHAPE, not a preference. `LayoutAllowedPage` is
    // `{pageKey, label}`, so for a listed page there is nothing here to derive a slug or a
    // route FROM and the payload is the only source; a class carries the whole descriptor,
    // so for a class member the payload is not needed and therefore is not read.
    //
    // Not reading it matters, because `pluginSlug` is not inert: `PluginWindowAdapter` renders
    // it as visible text in the error panel and in its testids. Taking it off the wire for a
    // key whose every other field is derived would leave one payload-chosen string inside a
    // window the snapshot otherwise only names — the same hole the title rule closes, one
    // field over. The listed-page arm keeps today's behaviour deliberately; widening the
    // allowlist so it can be derived there too is gh#2551.
    // ── AND A RENAMED ENTRY TAKES ITS CHROME FROM THE RENAME, NOT THE PAYLOAD ────
    // The payload's slug and route are the RETIRED ones; using them would restore the
    // window under the name the surface no longer has. See {@link RENAMED_PAGE_KEYS}.
    pluginSlug: runtime ? runtime.descriptor.pluginSlug : (renamed?.pluginSlug ?? e.pluginSlug),
    pageRoute: runtime ? runtime.descriptor.pageRoute : (renamed?.pageRoute ?? e.pageRoute),
    // Last resort is the key itself — a manifest page with a blank label must still produce a
    // legible title bar rather than an empty one. A class member's title comes from the class's
    // own derivation, which reads the KEY and never the payload — the same rule, one door over.
    label: runtime
      ? runtime.descriptor.label
      : allowed && typeof allowed.label === 'string' && allowed.label.length > 0
        ? allowed.label
        // THE CURRENT KEY, not the stored one: a renamed page's title bar must not still
        // say the name the surface retired.
        : pageKey,
    pos: geometry.pos,
    size: geometry.size,
    isMinimized: e.isMinimized === true,
    isMaximized: e.isMaximized === true,
    // A window's on-screen frame is derived from EXACTLY ONE state. An entry claiming both
    // maximize and dock, or a dock while minimized, describes no window this store can hold,
    // so the dock claim is dropped here rather than resolved by precedence later. Dropping it
    // also leaves the partner's group one member short, which dissolves that half too — the
    // pair fails together, which is the only way a split can fail without leaving a gap.
    dock:
      e.isMaximized === true || e.isMinimized === true ? null : toDockDescriptor(e.dock),
  };
}

/** Index an allowlist by pageKey. Own-key `Map`, so no prototype member can pose as a page. */
function indexAllowedPages(
  allowedPages: readonly LayoutAllowedPage[] | undefined,
): Map<string, LayoutAllowedPage> {
  const byPageKey = new Map<string, LayoutAllowedPage>();
  // A missing/!Array allowlist yields an EMPTY map — fail-closed: nothing is capturable or
  // applicable, rather than everything.
  if (!Array.isArray(allowedPages)) return byPageKey;
  for (const page of allowedPages) {
    if (!page || typeof page.pageKey !== 'string' || page.pageKey.length === 0) continue;
    if (byPageKey.has(page.pageKey)) continue; // first entry wins — deterministic
    byPageKey.set(page.pageKey, page);
  }
  return byPageKey;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useDesktopStore = create<DesktopState>((set, get) => ({
  windows: {},
  focusStack: [],
  layoutMode: 'free',
  // Enable FPS counter in dev mode by default
  fpsCounterEnabled: import.meta.env.DEV,
  pendingMutateRequest: null,

  addWindow(w) {
    const id = crypto.randomUUID();
    set((state) => {
      const focusStack = [...state.focusStack.filter((x) => x !== id), id];
      const zIndices = computeZIndices(focusStack);

      // Mark all existing windows as unfocused, new window is focused
      const updatedWindows: Record<WindowId, WindowState> = {};
      for (const [wid, win] of Object.entries(state.windows)) {
        updatedWindows[wid] = { ...win, zIndex: zIndices[wid] ?? win.zIndex, isFocused: false };
      }
      updatedWindows[id] = {
        ...w,
        id,
        zIndex: zIndices[id] ?? focusStack.length,
        isFocused: true,
        agentSlotOpen: w.agentSlotOpen ?? false,
        // Belt to the type's braces: the parameter type excludes `dock`, but the type only
        // binds callers this compiler can see. A seed carrying one would be a group of one.
        dock: undefined,
      };

      return { windows: updatedWindows, focusStack };
    });
    return id;
  },

  removeWindow(id) {
    // gh#1574 — read the window BEFORE the pure set() so we can tear down a per-open dynamic
    // pageKey after removal. Only close/remove reaches here (minimize keeps the window in the
    // store), so a still-live key is never dropped by minimize/restore/StrictMode churn.
    const removed = get().windows[id];
    set((state) => {
      // A closing window is leaving the screen, so its partner keeps the space the pair had.
      const dissolved = dissolveDockGroup(state.windows, id, 'survivor-keeps');
      const { [id]: _removed, ...rest } = dissolved;
      const focusStack = state.focusStack.filter((x) => x !== id);

      // Re-focus the new top window if any
      const updatedWindows: Record<WindowId, WindowState> = {};
      const topId = focusStack[focusStack.length - 1];
      const zIndices = computeZIndices(focusStack);
      for (const [wid, win] of Object.entries(rest)) {
        updatedWindows[wid] = {
          ...win,
          zIndex: zIndices[wid] ?? win.zIndex,
          isFocused: wid === topId,
        };
      }

      return { windows: updatedWindows, focusStack };
    });

    // gh#1574 — a per-open dynamic pageKey belongs to exactly this window; tear it down so
    // the registry returns to baseline. unregisterPageComponent is idempotent and refuses
    // core (static) keys, so this is safe even if ownsPageKey were ever set spuriously.
    if (removed?.ownsPageKey && removed.pageKey) {
      unregisterPageComponent(removed.pageKey);
    }
  },

  focusWindow(id) {
    set((state) => {
      if (!state.windows[id]) return state;

      // Pop + push to tail
      const focusStack = [...state.focusStack.filter((x) => x !== id), id];
      const zIndices = computeZIndices(focusStack);

      const updatedWindows: Record<WindowId, WindowState> = {};
      for (const [wid, win] of Object.entries(state.windows)) {
        updatedWindows[wid] = {
          ...win,
          zIndex: zIndices[wid] ?? win.zIndex,
          isFocused: wid === id,
        };
      }

      return { windows: updatedWindows, focusStack };
    });
  },

  moveWindow(id, pos) {
    set((state) => {
      const win = state.windows[id];
      if (!win) return state;
      // Naming a position is a request to LEAVE the group: the coordinates are outside the
      // tiling, so the pair unlinks and the move lands on the restored window. This is the
      // drag-out gesture every desktop has, and routing it through the same `return-both`
      // dissolve as `undockWindow` means there is one unsplit rule rather than two.
      //
      // It costs nothing on the hot path: a pointermove on an UNDOCKED window takes the same
      // branch it always took, and the dissolve runs once, on the first move of a drag.
      const windows = win.dock ? dissolveDockGroup(state.windows, id, 'return-both') : state.windows;
      return {
        windows: {
          ...windows,
          [id]: { ...windows[id], pos },
        },
      };
    });
  },

  resizeWindow(id, size) {
    set((state) => {
      const win = state.windows[id];
      if (!win) return state;
      // A docked window's size is DERIVED from the seam, and `setDockRatio` is its only size
      // control — writing a free size here would leave `dock` describing a split the two
      // windows no longer form. Refusing (rather than dissolving, as `moveWindow` does) is the
      // difference between the two gestures: a move names a place outside the tiling, while a
      // resize names a different tiling, which the seam already expresses.
      if (win.dock) return state;
      return {
        windows: {
          ...state.windows,
          [id]: { ...win, size },
        },
      };
    });
  },

  setLayoutMode(mode) {
    set({ layoutMode: mode });
  },

  toggleFpsCounter() {
    set((state) => ({ fpsCounterEnabled: !state.fpsCounterEnabled }));
  },

  // ── Faz 5 — window manager actions ────────────────────────────────────────

  closeWindow(id) {
    // Delegates to removeWindow for unified focus/zIndex recalculation
    get().removeWindow(id);
  },

  minimizeWindow(id) {
    set((state) => {
      const win = state.windows[id];
      if (!win) return state;
      // Off the screen is out of the split: the group dissolves first and the partner keeps
      // the space, then the minimize applies to the (restored) window.
      const dissolved = dissolveDockGroup(state.windows, id, 'survivor-keeps');
      // Minimized windows are removed from the focus stack
      const focusStack = state.focusStack.filter((x) => x !== id);
      const topId = focusStack[focusStack.length - 1];
      const zIndices = computeZIndices(focusStack);
      const updatedWindows: Record<WindowId, WindowState> = {};
      for (const [wid, w] of Object.entries(dissolved)) {
        updatedWindows[wid] = {
          ...w,
          zIndex: zIndices[wid] ?? w.zIndex,
          isFocused: wid === topId,
          isMinimized: wid === id ? true : w.isMinimized,
        };
      }
      return { windows: updatedWindows, focusStack };
    });
  },

  maximizeWindow(id) {
    set((state) => {
      const existing = state.windows[id];
      if (!existing || existing.isMaximized) return state;
      // Maximize is the other derived state, so the split has to end before it begins: the
      // partner keeps the group's space and this window returns to its pre-dock frame — which
      // is then what the maximize saves, so restoring lands where the window started.
      const windows = dissolveDockGroup(state.windows, id, 'survivor-keeps');
      const win = windows[id];
      // Save current pos/size for restore
      return {
        windows: {
          ...windows,
          [id]: {
            ...win,
            isMaximized: true,
            isMinimized: false,
            savedPos: win.pos,
            savedSize: win.size,
            // Position at origin; visual sizing via CSS in Faz 6+
            pos: { x: 0, y: 0 },
            size: { width: window.innerWidth, height: window.innerHeight },
          },
        },
      };
    });
  },

  restoreWindow(id) {
    set((state) => {
      const existing = state.windows[id];
      if (!existing) return state;
      // Restore means "leave whatever derived state you are in". For a docked window that is
      // the unsplit, and `return-both` IS the restore — after it the body below finds the
      // pre-dock frame already in place and changes nothing further.
      const windows = existing.dock
        ? dissolveDockGroup(state.windows, id, 'return-both')
        : state.windows;
      const win = windows[id];
      const restoredPos = win.savedPos ?? win.pos;
      const restoredSize = win.savedSize ?? win.size;
      return {
        windows: {
          ...windows,
          [id]: {
            ...win,
            isMaximized: false,
            isMinimized: false,
            pos: restoredPos,
            size: restoredSize,
            savedPos: undefined,
            savedSize: undefined,
          },
        },
      };
    });
  },

  cycleFocus() {
    set((state) => {
      const ids = Object.keys(state.windows);
      if (ids.length < 2) return state;
      // Cycle through windows in focusStack order (tail = current top)
      const stackIds = state.focusStack.filter((id) => {
        const w = state.windows[id];
        return w && !w.isMinimized;
      });
      if (stackIds.length < 2) return state;
      // Move head (oldest) to tail (newest / focused)
      const [first, ...rest] = stackIds;
      const focusStack = [...rest, first];
      const topId = focusStack[focusStack.length - 1];
      const zIndices = computeZIndices(focusStack);
      const updatedWindows: Record<WindowId, WindowState> = {};
      for (const [wid, w] of Object.entries(state.windows)) {
        updatedWindows[wid] = {
          ...w,
          zIndex: zIndices[wid] ?? w.zIndex,
          isFocused: wid === topId,
        };
      }
      return { windows: updatedWindows, focusStack };
    });
  },

  snapWindow(id, direction) {
    set((state) => {
      const existing = state.windows[id];
      if (!existing) return state;
      // DOCKED PRECONDITION ONLY — everything below is unchanged. A snap names a frame for
      // ONE window, so the link has to go first, and it goes the `return-both` way: nobody is
      // leaving the screen. The snap then reads the restored window, so `savedPos ?? pos`
      // saves the pre-DOCK frame rather than the half the window was occupying.
      const windows = existing.dock
        ? dissolveDockGroup(state.windows, id, 'return-both')
        : state.windows;
      const win = windows[id];
      const W = typeof window !== 'undefined' ? window.innerWidth : 1920;
      const H = typeof window !== 'undefined' ? window.innerHeight : 1080;
      const snapMap: Record<SnapDirection, { pos: { x: number; y: number }; size: { width: number; height: number } }> = {
        left: { pos: { x: 0, y: 0 }, size: { width: Math.floor(W / 2), height: H } },
        right: { pos: { x: Math.floor(W / 2), y: 0 }, size: { width: Math.floor(W / 2), height: H } },
        top: { pos: { x: 0, y: 0 }, size: { width: W, height: Math.floor(H / 2) } },
        bottom: { pos: { x: 0, y: Math.floor(H / 2) }, size: { width: W, height: Math.floor(H / 2) } },
        maximize: { pos: { x: 0, y: 0 }, size: { width: W, height: H } },
      };
      const { pos, size } = snapMap[direction];
      return {
        windows: {
          ...windows,
          [id]: {
            ...win,
            savedPos: win.savedPos ?? win.pos,
            savedSize: win.savedSize ?? win.size,
            pos,
            size,
            isMaximized: direction === 'maximize',
          },
        },
      };
    });
  },

  blurActiveWindow() {
    set((state) => {
      const topId = state.focusStack[state.focusStack.length - 1];
      if (!topId) return state;
      const updatedWindows: Record<WindowId, WindowState> = {};
      for (const [wid, w] of Object.entries(state.windows)) {
        updatedWindows[wid] = { ...w, isFocused: false };
      }
      return { windows: updatedWindows };
    });
  },

  // ── Faz 7 — per-window AI sidebar ─────────────────────────────────────────

  toggleAgentSlot(id) {
    set((state) => {
      const win = state.windows[id];
      if (!win) return state;
      return {
        windows: {
          ...state.windows,
          [id]: { ...win, agentSlotOpen: !win.agentSlotOpen },
        },
      };
    });
  },

  // ── G7-W PR-4 — pairwise split/dock (gh#2059) ─────────────────────────────

  dockWindow(id, targetId, edge) {
    set((state) => {
      const win = state.windows[id];
      const target = state.windows[targetId];
      if (!win || !target || id === targetId) return state;
      // Both halves of a split are on screen by construction.
      if (win.isMinimized || target.isMinimized) return state;
      const targetEdge = DOCK_COMPLEMENT.get(edge);
      if (!targetEdge) return state; // closed vocabulary

      // PAIRWISE. Docking a third window does not grow a group, it TAKES a member: whatever
      // group either window is in ends first, under the rule that fits — the abandoned
      // partner is not the one leaving, so it keeps the space (`survivor-keeps`). Re-docking
      // a pair to each other is the same path: the first dissolve unlinks both, and the
      // placement below re-forms the group on the new axis.
      let windows = dissolveDockGroup(state.windows, id, 'survivor-keeps');
      windows = dissolveDockGroup(windows, targetId, 'survivor-keeps');

      const groupId = crypto.randomUUID();
      const leadRatio = clampDockRatio(DOCK_DEFAULT_RATIO, isHorizontalEdge(edge));
      // ONE seam for the group; each member is handed its side of it.
      const rects = dockGroupRects(leadEdgeOfAxis(edge), leadRatio);

      const place = (member: WindowState, memberEdge: LayoutDockSlot): WindowState => {
        const leads = isLeadingEdge(memberEdge);
        const share = leads ? leadRatio : 1 - leadRatio;
        const rect = leads ? rects.lead : rects.trail;
        return {
          ...member,
          dock: { groupId, edge: memberEdge, ratio: share },
          // First save wins — the convention `snapWindow` already uses, so a window that was
          // snapped and is now docked still remembers the frame it started free in.
          savedPos: member.savedPos ?? member.pos,
          savedSize: member.savedSize ?? member.size,
          pos: rect.pos,
          size: rect.size,
          // One derived state at a time: a docked window's frame comes from the split.
          isMaximized: false,
        };
      };

      return {
        windows: {
          ...windows,
          [id]: place(windows[id], edge),
          [targetId]: place(windows[targetId], targetEdge),
        },
      };
    });
  },

  undockWindow(id) {
    set((state) => {
      if (!state.windows[id]?.dock) return state;
      return { windows: dissolveDockGroup(state.windows, id, 'return-both') };
    });
  },

  setDockRatio(groupId, ratio) {
    set((state) => {
      const memberIds = dockGroupMemberIds(state.windows, groupId);
      if (memberIds.length !== 2) return state;

      const leadId = memberIds.find((mid) => isLeadingEdge(state.windows[mid].dock!.edge));
      const trailId = memberIds.find((mid) => !isLeadingEdge(state.windows[mid].dock!.edge));
      if (!leadId || !trailId) return state;
      const leadEdge = state.windows[leadId].dock!.edge;
      const trailEdge = state.windows[trailId].dock!.edge;
      // Refuse a group that is not a complementary pair on ONE axis — `left` + `bottom` has a
      // leading and a trailing member and no seam. Nothing here creates one; refusing keeps a
      // hand-written state from being answered with an arbitrary geometry.
      if (DOCK_COMPLEMENT.get(leadEdge) !== trailEdge) return state;

      const leadShare = clampDockRatio(ratio, isHorizontalEdge(leadEdge));
      const rects = dockGroupRects(leadEdge, leadShare);
      const placements: Array<[WindowId, number, WindowRect]> = [
        [leadId, leadShare, rects.lead],
        [trailId, 1 - leadShare, rects.trail],
      ];

      const windows = { ...state.windows };
      for (const [memberId, share, rect] of placements) {
        const member = windows[memberId];
        windows[memberId] = {
          ...member,
          dock: { ...member.dock!, ratio: share },
          pos: rect.pos,
          size: rect.size,
        };
      }
      return { windows };
    });
  },

  fitDerivedFramesToViewport() {
    const viewport = viewportSize();
    set((state) => {
      let windows = state.windows;
      for (const win of Object.values(state.windows)) {
        if (!win.isMaximized || win.dock) continue;
        const { pos, size } = win;
        if (pos.x === 0 && pos.y === 0 && size.width === viewport.width && size.height === viewport.height) {
          continue;
        }
        windows = { ...windows, [win.id]: { ...win, pos: { x: 0, y: 0 }, size: { ...viewport } } };
      }
      return windows === state.windows ? state : { windows };
    });
    // A group is re-placed through its own seam, at the lead member's share.
    const { windows, setDockRatio } = get();
    for (const win of Object.values(windows)) {
      if (win.dock && isLeadingEdge(win.dock.edge)) setDockRatio(win.dock.groupId, win.dock.ratio);
    }
  },

  // ── Faz 10 — chat mutate request ──────────────────────────────────────────

  setPendingMutateRequest(req) {
    set({ pendingMutateRequest: req });
  },

  clearPendingMutateRequest() {
    set({ pendingMutateRequest: null });
  },

  reset() {
    set({ windows: {}, focusStack: [], pendingMutateRequest: null });
  },

  // ── G7-W PR-1 — layout snapshot (gh#2059) ─────────────────────────────────

  captureLayout(options) {
    // REFUSE rather than record a half-built allowlist: a snapshot taken while the manifest
    // pages are still loading (or after their fetch failed) looks exactly like a smaller
    // layout, and PR-2 would persist that as the truth. Returning null lets the caller retry;
    // an incomplete snapshot cannot be un-saved.
    if (options?.ready !== true) return null;

    const { windows, focusStack } = get();
    // The SAME gate apply reads: a page this viewer may not open is never recorded, so a
    // snapshot cannot carry a window that apply would then have to drop.
    const allowedByPageKey = indexAllowedPages(options.allowedPages);
    const runtimeClasses = options.runtimeClasses;

    // Bottom → top, so the array order alone already reproduces the stack.
    //
    // Two eligibility arms, and apply reads both the same way round: the allowlist, else a
    // runtime CLASS this viewer may open. `describeRuntimeWindow` rather than a bare prefix
    // hit, because a key its class cannot ADDRESS is one apply would refuse — and capture
    // must never record what apply refuses.
    const ranked = [...rankRepresentableWindows(windows, focusStack).values()]
      .filter(
        (r) =>
          allowedByPageKey.has(r.pageKey) ||
          describeRuntimeWindow(r.pageKey, runtimeClasses) !== undefined,
      )
      .sort((a, b) => a.rank - b.rank || a.insertion - b.insertion);

    // A dock group survives capture only WHOLE. Recording one half of a pair would record a
    // group `applyLayout` refuses — and "capture never records what apply would refuse" is the
    // property that keeps the two verbs from disagreeing about what a layout contains.
    const capturedGroupSizes = new Map<string, number>();
    for (const { win } of ranked) {
      const groupId = win.dock?.groupId;
      if (groupId) capturedGroupSizes.set(groupId, (capturedGroupSizes.get(groupId) ?? 0) + 1);
    }

    return {
      version: LAYOUT_SNAPSHOT_VERSION,
      windows: ranked.map(({ win, pageKey }) => {
        const dock =
          win.dock && capturedGroupSizes.get(win.dock.groupId) === 2 ? { ...win.dock } : undefined;
        // Maximized and docked windows record their RESTORE frame — the on-screen rect is a
        // function of this session's viewport, so re-applying it verbatim on a smaller screen
        // would "maximize" a window past the edges, or leave a split that no longer tiles.
        // A window whose dock was DROPPED just above records its on-screen frame instead:
        // with no discriminator to derive it from, the frame is all the entry can say, and a
        // docked half is a perfectly good free-window rect.
        const derived = win.isMaximized || dock !== undefined;
        return {
          pageKey,
          pluginSlug: win.pluginSlug,
          pageRoute: win.pageRoute,
          pos: { ...(derived ? win.savedPos ?? win.pos : win.pos) },
          size: { ...(derived ? win.savedSize ?? win.size : win.size) },
          isMinimized: win.isMinimized,
          isMaximized: win.isMaximized,
          ...(dock ? { dock } : {}),
        };
      }),
      // Minimized windows are not in focusStack (minimizeWindow pops them), so rank < 0.
      focusOrder: ranked.filter((r) => r.rank >= 0).map((r) => r.pageKey),
    };
  },

  applyLayout(snapshot, options) {
    const normalized = normalizeSnapshot(snapshot);
    if (!normalized) return; // wrong version / wrong shape → silent no-op

    // Same readiness contract as capture. Applying against a short allowlist only opens fewer
    // windows — but the user reads that half-restored desktop as THE layout, and the next
    // capture (once ready flips true) would then record the mutilated version.
    if (options?.ready !== true) return;

    const allowedByPageKey = indexAllowedPages(options.allowedPages);
    const runtimeClasses = options.runtimeClasses;
    // Nothing this viewer may open — through EITHER arm — so there is nothing to do. Both
    // have to be empty: a viewer with no listed pages could still be entitled to a class,
    // and returning on the allowlist alone would drop that half unread.
    if (allowedByPageKey.size === 0 && !(runtimeClasses && runtimeClasses.length > 0)) return;

    const state = get();
    // Same identity rule as capture: an already-open window for this pageKey is RE-USED
    // (re-positioned) instead of duplicated.
    const existingByPageKey = rankRepresentableWindows(state.windows, state.focusStack);
    const ownedPageKeys = collectOwnedPageKeys(state.windows);

    const focusRank = new Map<string, number>();
    normalized.focusOrder.forEach((key, index) => {
      if (!focusRank.has(key)) focusRank.set(key, index);
    });

    // PASS 1 — resolve entries. Pairing cannot be decided one entry at a time (a dock is only
    // real if the PARTNER also survived every gate), so the windows are built in pass 3, after
    // the groups are known.
    const resolved: Array<{
      entry: ApplicableEntry;
      id: WindowId;
      prev: WindowState | undefined;
    }> = [];
    const seen = new Set<string>();

    for (const raw of normalized.windows) {
      const entry = toApplicableEntry(raw, allowedByPageKey, ownedPageKeys, runtimeClasses);
      if (!entry) continue;
      // pageKey is the snapshot's identity — a repeated key would open the same page twice.
      if (seen.has(entry.pageKey)) continue;
      seen.add(entry.pageKey);

      const existing = existingByPageKey.get(entry.pageKey);
      resolved.push({ entry, id: existing?.id ?? crypto.randomUUID(), prev: existing?.win });
    }

    // Nothing applicable (every key unregistered / malformed) → leave the store untouched
    // rather than emitting a state change that re-renders every window for no reason.
    if (resolved.length === 0) return;

    // PASS 2 — decide the dock groups, and REMINT their identity.
    //
    // A group is honoured only when it is exactly what this store can hold: two members whose
    // edges are complementary. Everything else — a partner the allowlist refused, an
    // unregistered partner, a third member, two `left`s, an axis mismatch — dissolves the
    // whole claim, and both windows simply open at the frames their entries carry. That is the
    // fail-soft rule the layout gates use everywhere else, applied to the pair as a unit.
    //
    // The trailing share is RE-DERIVED as `1 - lead`, never read from the payload: two
    // independently-supplied ratios are two chances to describe halves that gap or overlap,
    // and the seam has to be one number. `groupId` is re-minted for the same reason a title is
    // never carried — an identity that came off the wire is an identity an attacker chose.
    const claimsByGroup = new Map<string, Array<(typeof resolved)[number]>>();
    for (const item of resolved) {
      const groupId = item.entry.dock?.groupId;
      if (!groupId) continue;
      const bucket = claimsByGroup.get(groupId);
      if (bucket) bucket.push(item);
      else claimsByGroup.set(groupId, [item]);
    }

    // Placement is decided per GROUP, not per member — one seam number, both sides.
    const placementById = new Map<WindowId, { dock: WindowDock; rect: WindowRect }>();
    for (const members of claimsByGroup.values()) {
      if (members.length !== 2) continue;
      const [first, second] = members;
      const firstEdge = first.entry.dock!.edge;
      const secondEdge = second.entry.dock!.edge;
      if (DOCK_COMPLEMENT.get(firstEdge) !== secondEdge) continue;

      const lead = isLeadingEdge(firstEdge) ? first : second;
      const trail = lead === first ? second : first;
      const groupId = crypto.randomUUID();
      const leadEdge = leadEdgeOfAxis(firstEdge);
      const leadShare = clampDockRatio(lead.entry.dock!.ratio, isHorizontalEdge(firstEdge));
      const rects = dockGroupRects(leadEdge, leadShare);
      placementById.set(lead.id, {
        dock: { groupId, edge: lead.entry.dock!.edge, ratio: leadShare },
        rect: rects.lead,
      });
      placementById.set(trail.id, {
        dock: { groupId, edge: trail.entry.dock!.edge, ratio: 1 - leadShare },
        rect: rects.trail,
      });
    }

    // PASS 3 — build the windows.
    const nextWindows: Record<WindowId, WindowState> = { ...state.windows };
    const applied: Array<{ id: WindowId; isMinimized: boolean; rank: number }> = [];

    for (const { entry, id, prev } of resolved) {
      const maximized = entry.isMaximized;
      const placement = placementById.get(id);
      const dock = placement?.dock;
      // Both derived states recompute their on-screen rect against THIS viewport and keep the
      // snapshot frame as the restore frame, so the traffic-light restore button still does
      // the right thing and a split still tiles on a screen it was not captured on.
      const derivedRect =
        placement?.rect ?? (maximized ? { pos: { x: 0, y: 0 }, size: viewportSize() } : null);

      nextWindows[id] = {
        id,
        pluginSlug: entry.pluginSlug,
        pageRoute: entry.pageRoute,
        pageKey: entry.pageKey,
        // Title is DERIVED, never carried by the payload: a live window's own title wins (it
        // is current), else the allowlist's canonical label, else the key — never an empty
        // title bar, and never an attacker-chosen string.
        title: prev?.title ?? entry.label,
        pos: derivedRect ? derivedRect.pos : { ...entry.pos },
        size: derivedRect ? derivedRect.size : { ...entry.size },
        // Re-use keeps the window's own restore frame in the free arm: a snapped window
        // (snapWindow writes savedPos/savedSize without maximizing) must not lose the frame it
        // snapped FROM just because a layout re-positioned it.
        savedPos: derivedRect ? { ...entry.pos } : prev?.savedPos,
        savedSize: derivedRect ? { ...entry.size } : prev?.savedSize,
        zIndex: prev?.zIndex ?? 0,
        isMinimized: entry.isMinimized,
        isMaximized: maximized,
        isFocused: false, // recomputed below from the rebuilt stack
        agentSlotOpen: prev?.agentSlotOpen ?? false,
        // A re-used window that the snapshot does not dock LOSES its live dock — the entry is
        // the whole description of this window's state. Its ex-partner is left holding a group
        // of one, which `sweepDockGroups` clears below.
        dock,
        // ownsPageKey is deliberately unset: everything applied here uses a STABLE registry
        // key, so nothing this action opens may unregister a component on close (gh#1574).
        // That covers a key REHYDRATED from a runtime class too — a class key is stable per
        // subject (one per saved view) and shared by every window on it, so it is exactly the
        // kind of key `unregisterPageComponent` must never be handed.
      };
      applied.push({
        id,
        isMinimized: entry.isMinimized,
        rank: focusRank.get(entry.pageKey) ?? -1,
      });
    }

    // Windows the snapshot does not mention are LEFT ALONE — apply is "lay these out", not
    // "replace the workspace"; closing them would throw away unsaved window state. Callers
    // that want a clean slate clear the store first.
    const appliedIds = new Set(applied.map((a) => a.id));
    const untouched = state.focusStack.filter((id) => !appliedIds.has(id) && nextWindows[id]);
    const restored = applied
      .filter((a) => !a.isMinimized)
      .sort((a, b) => a.rank - b.rank)
      .map((a) => a.id);

    const focusStack = [...untouched, ...restored];
    const zIndices = computeZIndices(focusStack);
    const topId = focusStack[focusStack.length - 1];

    const stacked: Record<WindowId, WindowState> = {};
    for (const [wid, win] of Object.entries(nextWindows)) {
      stacked[wid] = {
        ...win,
        zIndex: zIndices[wid] ?? win.zIndex,
        isFocused: wid === topId,
      };
    }
    // The one bulk write in this store, and so the one place a live group can be stranded by a
    // partner being re-laid-out. Sweep before the write, never after: a set() that publishes a
    // half-group would be a frame the compositor renders.
    const windows = sweepDockGroups(stacked);

    // ONE write: the DOM is a pure projection of this store (useWindowDomSync writes
    // transform/size from state in a layout effect and never writes back), so a single set()
    // lands the whole layout in one render — no intermediate frame where half the windows
    // have moved and the focus ring is on the wrong one.
    set({ windows, focusStack });
  },
}));
